import { Button, CircularProgress, Stack, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { QueryUsersOutput, UserEmailVerified } from '@cnx-saas/common';
import { TABLE_ITEMS_PER_PAGE } from '@cnx-saas/common-fe';
import { useFindUsers } from '../../../api/tenant';
import { ExistingUserTable } from './ExistingUserTable';
import { Filters, useFilters } from './Filters';
import { TableRowCount } from './TableRowCount';

interface ExistingUsersProps {
  onRefetch: (refetch: () => void) => void;
}

export const ExistingUsers = ({ onRefetch }: ExistingUsersProps) => {
  const { t } = useTranslation();
  const [tablePage, setTablePage] = React.useState(0);

  const {
    firstNameProps,
    lastNameProps,
    emailProps,
    userTypeProps,
    statusProps,
    userFilters,
    filtersActive,
    clearAllFilters,
  } = useFilters();

  const { data, previousData, loading, refetch } = useFindUsers({
    take: TABLE_ITEMS_PER_PAGE,
    skip: TABLE_ITEMS_PER_PAGE * tablePage,
    filters: [
      {
        emailVerified: UserEmailVerified.VERIFIED,
        ...userFilters,
      },
    ],
  });

  const [resolvedData, setResolvedData] = useState<
    QueryUsersOutput | undefined
  >(data ?? previousData);

  useEffect(() => {
    setResolvedData(data ?? previousData);
  }, [data, previousData]);

  useEffect(() => {
    onRefetch(refetch);
  }, [refetch, onRefetch]);

  return (
    <div>
      {resolvedData ? (
        <Container>
          {filtersActive && (
            <Button
              variant="text"
              color="error"
              onClick={clearAllFilters}
              sx={{ gridArea: 'actions', justifySelf: 'end' }}
            >
              {t('userManagement.label.clearFiltersButton', {
                defaultValue: 'Clear all filters',
              })}
            </Button>
          )}
          {resolvedData?.users.filteredCount > 0 && (
            <TableRowCount
              text={`${resolvedData.users.filteredCount} ${t(
                'userManagement.label.rowCounts',
                {
                  defaultValue: 'Users',
                },
              )}`}
            />
          )}
          <Filters
            firstNameProps={firstNameProps}
            lastNameProps={lastNameProps}
            emailProps={emailProps}
            userTypeProps={userTypeProps}
            statusProps={statusProps}
          />
          <ExistingUserTable
            users={resolvedData.users.itemList}
            pagination={{
              page: tablePage,
              totalPages: Math.ceil(
                resolvedData.users.filteredCount / TABLE_ITEMS_PER_PAGE,
              ),
              onPageChange: setTablePage,
            }}
            loading={loading}
            refetch={refetch}
          />
        </Container>
      ) : (
        <Stack height={1}>
          <CircularProgress sx={{ margin: 'auto' }} />
        </Stack>
      )}
    </div>
  );
};
const Container = styled('div')({
  height: '80vh',
  display: 'grid',
  grid: `".            .             actions" auto
         "filters      filters       filters" auto
         "row-counts   row-counts    row-counts" auto 
         "table        table         table"   1fr
         / auto        auto          1fr`,
  gap: '8px 16px',
  paddingTop: 16,
});
